import { useState, useEffect } from 'react';
import { Card, Badge, Button, Select, Input, DateInput } from '@atoms/index';
import { AlertModal } from '@/components/organisms/AlertModal';
import { useAdminOrders, useOrderAlerts } from '@/hooks/useAdminOrders';
import { adminOrderService, AdminOrderQuery, AdminUpdateOrderStatusDto, AdminReassignCourierDto } from '@services/adminOrderService';
import { adminService } from '@services/adminService';
import { formatCurrency } from '@/utils/currency';
import { cn } from '@/utils/cn';
import type { Order, OrderStatus } from '@/types';
import { useSocket } from '@/hooks/useSocket';

export default function OrdersTab() {
  const [filters, setFilters] = useState<AdminOrderQuery>({
    page: 1,
    limit: 20,
  });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [statusUpdateModal, setStatusUpdateModal] = useState<{ orderId: string; isOpen: boolean }>({
    orderId: '',
    isOpen: false,
  });
  const [reassignModal, setReassignModal] = useState<{ orderId: string; isOpen: boolean }>({
    orderId: '',
    isOpen: false,
  });
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; variant: 'default' | 'danger' | 'warning' | 'success' }>({ isOpen: false, title: '', message: '', variant: 'default' });

  const { orders, total, statusCounts, isLoading, error, refetch } = useAdminOrders(filters);
  const { alerts } = useOrderAlerts();
  const { isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;

    const handleOrderStatusChanged = (data: { orderId: string; status: OrderStatus }) => {
      console.log('Order status changed:', data);
    };

    return () => {};
  }, [isConnected]);

  const handleStatusFilter = (status: OrderStatus | 'ALL') => {
    setFilters((prev) => ({
      ...prev,
      status: status === 'ALL' ? undefined : status,
      unassignedOnly: false,
      page: 1,
    }));
  };

  const handleUnassignedToggle = (checked: boolean) => {
    setFilters((prev) => ({
      ...prev,
      unassignedOnly: checked || undefined,
      status: checked ? undefined : prev.status,
      page: 1,
    }));
  };

  const handleMinWaitMinutes = (minutes: number | '') => {
    setFilters((prev) => ({
      ...prev,
      minWaitMinutes: minutes === '' ? undefined : Number(minutes),
      page: 1,
    }));
  };

  const handleSearch = (search: string) => {
    setFilters((prev) => ({
      ...prev,
      search: search || undefined,
      page: 1,
    }));
  };

  const handleDateRange = (startDate?: string, endDate?: string) => {
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
      page: 1,
    }));
  };

  const handleViewDetails = async (orderId: string) => {
    try {
      const order = await adminOrderService.getOrderDetails(orderId);
      setSelectedOrder(order as any);
      setIsDetailModalOpen(true);
    } catch (error) {
      console.error('Failed to load order details:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Sipariş detayları yüklenemedi', variant: 'danger' });
    }
  };

  const handleUpdateStatus = async (orderId: string, status: OrderStatus, reason?: string) => {
    try {
      const previousStatus = selectedOrder?.status;
      await adminOrderService.updateOrderStatus(orderId, { status, reason });
      setStatusUpdateModal({ orderId: '', isOpen: false });
      setIsDetailModalOpen(false);
      setSelectedOrder(null);
      refetch();

      if (status === 'CANCELLED') {
        const isCreditCard = selectedOrder?.paymentMethod === 'CREDIT_CARD';
        setAlertState({
          isOpen: true,
          title: 'Sipariş İptal Edildi',
          message: isCreditCard
            ? 'Sipariş başarıyla iptal edildi. Ödeme iadesi Iyzico üzerinden otomatik olarak başlatıldı.'
            : 'Sipariş başarıyla iptal edildi.',
          variant: 'success',
        });
        return;
      }

      const isForceDelivered =
        status === 'DELIVERED' &&
        previousStatus !== 'ON_WAY' &&
        previousStatus !== 'DELIVERED';

      setAlertState({
        isOpen: true,
        title: 'Başarılı',
        message: isForceDelivered
          ? 'Sipariş doğrudan teslim edildi olarak işaretlendi.'
          : 'Sipariş durumu güncellendi.',
        variant: 'success',
      });
    } catch (error: any) {
      setAlertState({ isOpen: true, title: 'Hata', message: error.response?.data?.error?.message || 'Sipariş durumu güncellenirken bir hata oluştu.', variant: 'danger' });
    }
  };

  const handleReassignCourier = async (orderId: string, courierId: string) => {
    try {
      const dto: AdminReassignCourierDto = { courierId };
      await adminOrderService.reassignCourier(orderId, dto);
      setReassignModal({ orderId: '', isOpen: false });
      setIsDetailModalOpen(false);
      setSelectedOrder(null);
      refetch();
      setAlertState({ isOpen: true, title: 'Başarılı', message: 'Kurye yeniden atandı', variant: 'success' });
    } catch (error: any) {
      setAlertState({ isOpen: true, title: 'Hata', message: error.response?.data?.error?.message || 'Kurye atanamadı', variant: 'danger' });
    }
  };

  const totalPages = Math.ceil(total / (filters.limit || 20));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Sipariş Yönetimi</h2>
          <p className="text-gray-600">Tüm siparişleri görüntüleyin ve yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-3 h-3 rounded-full',
              isConnected ? 'bg-green-500' : 'bg-red-500',
            )}
          />
          <span className="text-sm text-gray-600">
            {isConnected ? 'Bağlı' : 'Bağlantı Kesildi'}
          </span>
        </div>
      </div>

      {alerts.length > 0 && (
        <Card className="bg-warning/10 border border-warning/20">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-warning mb-2">⚠️ Kritik Uyarılar</h3>
              <div className="space-y-1">
                {alerts.slice(0, 3).map((alert) => (
                  <p key={alert.orderId} className="text-sm text-gray-700">
                    {alert.orderNumber}: {alert.issue}
                  </p>
                ))}
              </div>
            </div>
            <Badge variant="warning">{alerts.length} Uyarı</Badge>
          </div>
        </Card>
      )}

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <Select
            label="Durum"
            value={filters.status || 'ALL'}
            onChange={(value) => handleStatusFilter(value as OrderStatus | 'ALL')}
            options={[
              { value: 'ALL', label: 'Tümü' },
              { value: 'CREATED', label: 'Yeni' },
              { value: 'PREPARING', label: 'Hazırlanıyor' },
              { value: 'READY', label: 'Hazır' },
              { value: 'ON_WAY', label: 'Yolda' },
              { value: 'DELIVERED', label: 'Teslim Edildi' },
              { value: 'CANCELLED', label: 'İptal' },
            ]}
            size="md"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => handleDateRange(e.target.value || undefined, filters.endDate)}
              className="w-full px-5 py-3 border border-gray-200 rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary hover:border-gray-300 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => handleDateRange(filters.startDate, e.target.value || undefined)}
              className="w-full px-5 py-3 border border-gray-200 rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary hover:border-gray-300 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ara</label>
            <input
              type="text"
              placeholder="Sipariş no, müşteri adı..."
              value={filters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-5 py-3 border border-gray-200 rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary hover:border-gray-300 bg-white placeholder:text-gray-400"
            />
          </div>

          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!filters.unassignedOnly}
                onChange={(e) => handleUnassignedToggle(e.target.checked)}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Kurye atanmamış</span>
            </label>
            {filters.unassignedOnly && (
              <div className="flex items-center gap-1">
                <label className="text-sm text-gray-600">En az</label>
                <input
                  type="number"
                  min={0}
                  placeholder="0"
                  value={filters.minWaitMinutes ?? ''}
                  onChange={(e) => handleMinWaitMinutes(e.target.value)}
                  className="w-16 px-2 py-2 border border-gray-200 rounded-xl text-sm"
                />
                <span className="text-sm text-gray-600">dk bekleyen</span>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-6 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{statusCounts.CREATED}</p>
            <p className="text-sm text-gray-600">Yeni</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-warning">{statusCounts.PREPARING}</p>
            <p className="text-sm text-gray-600">Hazırlanıyor</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-info">{statusCounts.READY}</p>
            <p className="text-sm text-gray-600">Hazır</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-secondary">{statusCounts.ON_WAY}</p>
            <p className="text-sm text-gray-600">Yolda</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-success">{statusCounts.DELIVERED}</p>
            <p className="text-sm text-gray-600">Teslim Edildi</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-600">{statusCounts.CANCELLED}</p>
            <p className="text-sm text-gray-600">İptal</p>
          </div>
        </Card>
      </div>

      {isLoading && (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Siparişler yükleniyor...</p>
        </div>
      )}

      {error && (
        <Card className="bg-warning/10 border border-warning/20">
          <p className="text-warning">{error}</p>
        </Card>
      )}

      {!isLoading && !error && (
        <>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left font-semibold px-6 py-3">Sipariş No</th>
                    <th className="text-left font-semibold px-6 py-3">Müşteri</th>
                    <th className="text-left font-semibold px-6 py-3">Restoran</th>
                    <th className="text-left font-semibold px-6 py-3">Durum</th>
                    <th className="text-left font-semibold px-6 py-3">Tutar</th>
                    <th className="text-left font-semibold px-6 py-3">Tarih</th>
                    <th className="text-right font-semibold px-6 py-3">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.length === 0 ? (
                    <tr>
                      <td className="px-6 py-10 text-center text-gray-500" colSpan={7}>
                        {filters.search
                          ? 'Sipariş veya müşteri bulunamadı.'
                          : 'Sipariş bulunamadı.'}
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-gray-900 font-medium">
                          {order.orderNumber || order.id.slice(0, 8)}
                        </td>
                        <td className="px-6 py-3 text-gray-700">
                          {(order as any).user?.name || (order as any).user?.phone || '—'}
                        </td>
                        <td className="px-6 py-3 text-gray-700">
                          {(order as any).restaurant?.name || '—'}
                        </td>
                        <td className="px-6 py-3">
                          <Badge
                            variant={
                              order.status === 'DELIVERED'
                                ? 'success'
                                : order.status === 'CANCELLED'
                                ? 'danger'
                                : order.status === 'PREPARING' || order.status === 'ON_WAY'
                                ? 'warning'
                                : 'info'
                            }
                          >
                            {order.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-3 text-gray-900 font-semibold">
                          {formatCurrency(
                            typeof order.total === 'number' && !isNaN(order.total)
                              ? order.total
                              : (order.subtotal || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0) || 0
                          )}
                        </td>
                        <td className="px-6 py-3 text-gray-600">
                          {new Date(order.createdAt).toLocaleString('tr-TR')}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleViewDetails(order.id)}
                            >
                              Detay
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
                disabled={(filters.page || 1) === 1}
              >
                ← Önceki
              </Button>
              <span className="px-4 py-2 text-gray-700">
                Sayfa {filters.page || 1} / {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFilters((prev) => ({ ...prev, page: Math.min(totalPages, (prev.page || 1) + 1) }))}
                disabled={(filters.page || 1) === totalPages}
              >
                Sonraki →
              </Button>
            </div>
          )}
        </>
      )}

      {isDetailModalOpen && selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedOrder(null);
          }}
          onUpdateStatus={(status, reason) => handleUpdateStatus(selectedOrder.id, status, reason)}
          onReassignCourier={(courierId) => handleReassignCourier(selectedOrder.id, courierId)}
        />
      )}

      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
}

interface OrderDetailModalProps {
  order: Order & { timeline?: any[]; restaurant?: any; courier?: any; user?: any };
  onClose: () => void;
  onUpdateStatus: (status: OrderStatus, reason?: string) => void;
  onReassignCourier: (courierId: string) => void;
}

const TIMELINE_ACTION_LABELS: Record<string, string> = {
  ADMIN_ORDER_STATUS_UPDATE: 'Durum Güncellendi',
  ADMIN_ORDER_FORCE_DELIVERED: 'Admin: Doğrudan Teslim Edildi',
  ADMIN_COURIER_REASSIGN: 'Kurye Yeniden Atandı',
};

const ORDER_STATUS_LABELS: Record<string, string> = {
  CREATED: 'Oluşturuldu',
  SCHEDULED: 'Planlandı',
  PENDING: 'Beklemede',
  PREPARING: 'Hazırlanıyor',
  READY: 'Hazır',
  ON_WAY: 'Yolda',
  DELIVERED: 'Teslim Edildi',
  CANCELLED: 'İptal Edildi',
};

function getTimelineEventLabel(event: { action?: string; metadata?: { previousStatus?: string; newStatus?: string } }): string {
  if (event.action && TIMELINE_ACTION_LABELS[event.action]) {
    return TIMELINE_ACTION_LABELS[event.action];
  }

  const previousStatus = event.metadata?.previousStatus;
  const newStatus = event.metadata?.newStatus;
  if (previousStatus && newStatus) {
    const fromLabel = ORDER_STATUS_LABELS[previousStatus] || previousStatus;
    const toLabel = ORDER_STATUS_LABELS[newStatus] || newStatus;
    return `${fromLabel} → ${toLabel}`;
  }

  return event.action || 'Durum Değişikliği';
}

function OrderDetailModal({ order, onClose, onUpdateStatus, onReassignCourier }: OrderDetailModalProps) {
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus>(order.status);
  const [reason, setReason] = useState('');
  const [courierId, setCourierId] = useState('');
  const [couriers, setCouriers] = useState<{ id: string; name?: string; phone?: string }[]>([]);
  const [couriersLoading, setCouriersLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState(false);

  useEffect(() => {
    if (!order.courierId) {
      setCouriersLoading(true);
      adminService
        .getCouriers(1, 100)
        .then((res: any) => {
          const list = res?.couriers ?? res ?? [];
          setCouriers(Array.isArray(list) ? list : []);
        })
        .finally(() => setCouriersLoading(false));
    }
  }, [order.courierId]);

  const totalAmount =
    typeof order.total === 'number' && !isNaN(order.total)
      ? order.total
      : (order.subtotal || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0) || 0;

  const statusConfig = {
    CREATED: { label: 'Oluşturuldu', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    PREPARING: { label: 'Hazırlanıyor', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    READY: { label: 'Hazır', color: 'bg-orange-100 text-orange-800 border-orange-200' },
    ON_WAY: { label: 'Yolda', color: 'bg-purple-100 text-purple-800 border-purple-200' },
    DELIVERED: { label: 'Teslim Edildi', color: 'bg-green-100 text-green-800 border-green-200' },
    CANCELLED: { label: 'İptal Edildi', color: 'bg-red-100 text-red-800 border-red-200' },
  };

  const statusInfo = statusConfig[order.status] || statusConfig.CREATED;

  const paymentMethodLabels: Record<string, string> = {
    CREDIT_CARD: 'Kredi Kartı',
    CASH: 'Nakit',
  };

  const paymentStatusConfig = {
    PAID: { label: 'Ödendi', color: 'bg-green-100 text-green-800 border-green-200' },
    PENDING: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    FAILED: { label: 'Başarısız', color: 'bg-red-100 text-red-800 border-red-200' },
    REFUNDED: { label: 'İade Edildi', color: 'bg-gray-100 text-gray-800 border-gray-200' },
  };

  const paymentStatusInfo = paymentStatusConfig[order.paymentStatus] || paymentStatusConfig.PENDING;
  const canForceDeliver = order.status !== 'DELIVERED' && order.status !== 'CANCELLED';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <Card className="max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="bg-gradient-to-r from-[#E63946] to-[#C1121F] text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-1">
                Sipariş #{order.orderNumber || order.id.slice(0, 8).toUpperCase()}
              </h3>
              <p className="text-white/90 text-sm">
                {new Date(order.createdAt).toLocaleDateString('tr-TR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 bg-[#F9FAFB]">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <span className="text-xl">📦</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Durum</p>
                    <p className={`text-sm font-semibold px-3 py-1 rounded-lg border inline-block mt-1 ${statusInfo.color}`}>
                      {statusInfo.label}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Toplam Tutar</p>
                    <p className="text-xl font-bold text-[#E63946] mt-1">{formatCurrency(totalAmount)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <span className="text-xl">💳</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Müşteri Ödemesi</p>
                    <p className={`text-sm font-semibold px-3 py-1 rounded-lg border inline-block mt-1 ${paymentStatusInfo.color}`}>
                      {paymentStatusInfo.label}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
                    <span className="text-lg">👤</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-lg">Müşteri Bilgileri</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 text-sm min-w-[80px]">İsim:</span>
                    <span className="text-gray-900 font-medium">{order.user?.name || order.user?.phone || '—'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 text-sm min-w-[80px]">Telefon:</span>
                    <span className="text-gray-900 font-medium">{order.user?.phone || '—'}</span>
                  </div>
                  {order.user?.email && (
                    <div className="flex items-start gap-3">
                      <span className="text-gray-400 text-sm min-w-[80px]">Email:</span>
                      <span className="text-gray-900 font-medium">{order.user.email}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                    <span className="text-lg">🍽️</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-lg">Restoran</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 text-sm min-w-[80px]">İsim:</span>
                    <span className="text-gray-900 font-medium">{order.restaurant?.name || '—'}</span>
                  </div>
                  {order.restaurant?.phone && (
                    <div className="flex items-start gap-3">
                      <span className="text-gray-400 text-sm min-w-[80px]">Telefon:</span>
                      <span className="text-gray-900 font-medium">{order.restaurant.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {order.courier && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                    <span className="text-lg">🚴</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-lg">Kurye Bilgileri</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">İsim</p>
                    <p className="text-gray-900 font-medium">{order.courier.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Telefon</p>
                    <p className="text-gray-900 font-medium">{order.courier.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Durum</p>
                    <Badge variant={order.courier.status === 'ONLINE' ? 'success' : 'warning'}>
                      {order.courier.status}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            {(order.customerNotes || (order as any).customer_notes) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-red-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                    <span className="text-lg">📝</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-lg">Sipariş Notu</h4>
                </div>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-gray-800 font-medium">{order.customerNotes || (order as any).customer_notes}</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                  <span className="text-lg">🛒</span>
                </div>
                <h4 className="font-bold text-gray-900 text-lg">Sipariş İçeriği</h4>
              </div>
              <div className="space-y-3">
                {((order.items || (order as any).orderItems) || []).map((item: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-sm font-bold text-[#E63946]">
                        {item.quantity}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.menuItemName || item.productName || 'Ürün'}</p>
                        {item.optionNames && item.optionNames.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {item.optionNames.map((opt: any) => (typeof opt === 'string' ? opt : opt.name || opt)).join(', ')}
                          </p>
                        )}
                        {item.removedIngredientNames && item.removedIngredientNames.length > 0 && (
                          <p className="text-xs text-red-600 mt-1 font-medium">
                            Çıkarılan: {item.removedIngredientNames.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="font-bold text-gray-900">{formatCurrency((item.unitPrice || item.price || 0) * (item.quantity || 1))}</p>
                  </div>
                ))}
                {(!order.items && !(order as any).orderItems) && (
                  <p className="text-gray-500 text-sm text-center py-4">Ürün bilgisi bulunamadı</p>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-gray-600">Ara Toplam</span>
                  <span className="font-medium">{formatCurrency(order.subtotal || 0)}</span>
                </div>
                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-gray-600">Teslimat Ücreti</span>
                  <span className="font-medium">{formatCurrency(order.deliveryFee || 0)}</span>
                </div>
                {order.serviceFee && order.serviceFee > 0 && (
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-gray-600">Servis Ücreti</span>
                    <span className="font-medium">{formatCurrency(order.serviceFee)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-200 mt-3">
                  <span className="font-bold text-lg text-gray-900">Toplam</span>
                  <span className="font-bold text-xl text-[#E63946]">{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <span className="text-lg">📍</span>
                </div>
                <h4 className="font-bold text-gray-900 text-lg">Teslimat Adresi</h4>
              </div>
              <p className="text-gray-700 leading-relaxed">{(order as any).deliveryAddressText || order.deliveryAddress?.fullAddress || '—'}</p>
            </div>

            {order.timeline && order.timeline.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                    <span className="text-lg">⏱️</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-lg">Zaman Çizelgesi</h4>
                </div>
                <div className="space-y-3">
                  {order.timeline.map((event: any, idx: number) => (
                    <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-2 h-2 bg-[#E63946] rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{getTimelineEventLabel(event)}</p>
                        {event.metadata?.reason && (
                          <p className="text-sm text-gray-600 mt-1">Sebep: {event.metadata.reason}</p>
                        )}
                        {event.metadata?.skippedIntermediateStates && (
                          <p className="text-xs text-amber-700 mt-1">Ara durumlar atlanarak doğrudan teslim edildi.</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(event.timestamp || event.createdAt).toLocaleString('tr-TR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
              <h4 className="font-bold text-gray-900 text-lg mb-4">Yönetim İşlemleri</h4>
              <div className="space-y-4">
                {canForceDeliver && !showCancelConfirm && (
                  <div className="rounded-2xl border border-[#2A9D8F]/30 bg-[#2A9D8F]/5 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">Doğrudan Teslim Et</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Siparişi ara durumlara geçirmeden tek adımda teslim edildi olarak işaretleyin.
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onUpdateStatus('DELIVERED', reason.trim() || undefined)}
                        className="px-6 bg-[#2A9D8F] hover:bg-[#238579] border-none whitespace-nowrap"
                      >
                        Teslim Edildi Olarak İşaretle
                      </Button>
                    </div>
                  </div>
                )}
                {!showCancelConfirm ? (
                <div>
                  <div className="flex gap-2">
                    <Select
                      value={selectedStatus}
                      onChange={(value) => {
                        const s = value as OrderStatus;
                        setSelectedStatus(s);
                        if (s === 'CANCELLED') {
                          setShowCancelConfirm(true);
                          setCancelReason('');
                          setCancelReasonError(false);
                        }
                      }}
                      options={[
                        { value: 'CREATED', label: 'Oluşturuldu' },
                        { value: 'PREPARING', label: 'Hazırlanıyor' },
                        { value: 'READY', label: 'Hazır' },
                        { value: 'ON_WAY', label: 'Yolda' },
                        { value: 'DELIVERED', label: 'Teslim Edildi' },
                        { value: 'CANCELLED', label: '⚠️ İptal Et / İade' },
                      ]}
                      placeholder="Durum seçin"
                      className="flex-1"
                      size="md"
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onUpdateStatus(selectedStatus, reason)}
                      disabled={selectedStatus === 'CANCELLED'}
                      className="px-6"
                    >
                      Güncelle
                    </Button>
                  </div>
                  <textarea
                    placeholder="Sebep (opsiyonel)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full mt-2 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E63946] focus:border-[#E63946] transition-all resize-none"
                    rows={2}
                  />
                </div>
                ) : (
                <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">⚠️</span>
                    </div>
                    <div>
                      <p className="font-bold text-red-800 text-base">Sipariş İptali / İade</p>
                      <p className="text-sm text-red-600">
                        {order.paymentMethod === 'CREDIT_CARD'
                          ? `${formatCurrency(totalAmount)} tutarında otomatik iade başlatılacaktır.`
                          : 'Kapıda ödeme — iade işlemi gerekmez.'}
                      </p>
                    </div>
                  </div>

                  {order.paymentMethod === 'CREDIT_CARD' && (
                    <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-red-200">
                      <span className="text-2xl">💳</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">İade Tutarı</p>
                        <p className="text-lg font-bold text-[#E63946]">{formatCurrency(totalAmount)}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Iyzico üzerinden otomatik iade — 5–10 iş günü</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-red-800 mb-1.5">
                      İptal Sebebi <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      placeholder="İptal sebebini açıklayın (zorunlu)..."
                      value={cancelReason}
                      onChange={(e) => {
                        setCancelReason(e.target.value);
                        if (e.target.value.trim()) setCancelReasonError(false);
                      }}
                      className={cn(
                        'w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all resize-none text-sm',
                        cancelReasonError
                          ? 'border-red-400 bg-red-50 focus:border-red-500'
                          : 'border-gray-300 bg-white focus:border-[#E63946]',
                      )}
                      rows={3}
                    />
                    {cancelReasonError && (
                      <p className="text-xs text-red-600 mt-1 font-medium">
                        İptal sebebi zorunludur. Lütfen bir açıklama girin.
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Bu bilgi müşteriye gösterilecektir.
                    </p>
                  </div>

                  <div className="flex gap-3 pt-1">
                    <Button
                      variant="danger"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        if (!cancelReason.trim()) {
                          setCancelReasonError(true);
                          return;
                        }
                        onUpdateStatus('CANCELLED', cancelReason.trim());
                        setShowCancelConfirm(false);
                      }}
                    >
                      ✓ İptali Onayla
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        setShowCancelConfirm(false);
                        setSelectedStatus(order.status);
                        setCancelReason('');
                        setCancelReasonError(false);
                      }}
                    >
                      Vazgeç
                    </Button>
                  </div>
                </div>
                )}

                {!order.courierId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Kurye Ata (Manuel)</label>
                    <div className="flex gap-2">
                      <Select
                        value={courierId}
                        onChange={(value) => setCourierId(value)}
                        options={[
                          { value: '', label: couriersLoading ? 'Yükleniyor...' : 'Kurye seçin' },
                          ...couriers.map((c: any) => {
                            const name = c.user?.name || c.name || 'Kurye';
                            const phone = c.user?.phone || c.phone;
                            return {
                              value: c.id,
                              label: `${name}${phone ? ` (${phone})` : ''}`.trim() || c.id.slice(0, 8),
                            };
                          }),
                        ]}
                        placeholder="Kurye seçin"
                        className="flex-1"
                        size="md"
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          if (courierId) {
                            onReassignCourier(courierId);
                            setCourierId('');
                          }
                        }}
                        disabled={!courierId || couriersLoading}
                        className="px-6"
                      >
                        Ata
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Hiç kurye kabul etmediğinde siparişi manuel olarak atayabilirsiniz.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
